// Ops categories: slug from the name, validation, ordering and the "in use" delete rule
// (contract: delete only when no product uses it; deactivate instead). Pure.

/** @typedef {import('@popcart/core/v1').CategoryView} CategoryView */
/** @typedef {import('@popcart/core/v1').CategoryInput} CategoryInput */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */

export const SLUG_RE = /^[a-z0-9-]{2,40}$/;
export const NAME_MAX = 40;

/** "Fruit & Veg" → "fruit-veg", "Ulam (Meals)" → "ulam-meals". @param {string} name */
export function slugify(name) {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/**
 * @typedef {object} CategoryDraft
 * @property {string} name
 * @property {ProductLine | ''} line
 * @property {string} sort
 * @property {boolean} isActive
 */

/** @returns {CategoryDraft} */
export function emptyCategory() {
  return { name: '', line: '', sort: '0', isActive: true };
}

/** @param {CategoryView} c @returns {CategoryDraft} */
export function categoryDraft(c) {
  return { name: c.name, line: c.line ?? '', sort: String(c.sort ?? 0), isActive: c.isActive };
}

/**
 * @param {CategoryDraft} d
 * @param {CategoryView[]} existing all categories (for the unique slug check)
 * @param {string | null} selfId the category being edited
 * @returns {Record<string, string>}
 */
export function validateCategory(d, existing, selfId) {
  /** @type {Record<string, string>} */
  const e = {};
  const name = d.name.trim();
  const slug = slugify(name);
  if (!name) e.name = 'Give the category a name.';
  else if (name.length > NAME_MAX) e.name = `Keep the name to ${NAME_MAX} characters.`;
  else if (!SLUG_RE.test(slug)) e.name = 'Use at least two letters or numbers in the name.';
  else {
    const clash = existing.find((c) => c.id !== selfId && c.slug === slug);
    if (clash) e.name = `“${clash.name}” already uses that name.`;
  }
  if (d.line !== 'fresh' && d.line !== 'grill' && d.line !== 'mart') e.line = 'Pick the line it belongs to.';
  const sort = d.sort.trim() === '' ? 0 : Number(d.sort.trim());
  if (!Number.isInteger(sort) || Math.abs(sort) > 9999) e.sort = 'Sort is a whole number (lower shows first).';
  return e;
}

/** @param {CategoryDraft} d @returns {CategoryInput} */
export function buildCategoryInput(d) {
  return {
    name: d.name.trim(),
    slug: slugify(d.name.trim()),
    line: /** @type {ProductLine} */ (d.line),
    sort: d.sort.trim() === '' ? 0 : Number(d.sort.trim()),
    isActive: d.isActive,
  };
}

/**
 * Only the changed fields. The slug follows the name (it's what customer filters link to, so a
 * rename moves it too); unchanged names keep their slug.
 * @param {CategoryDraft} d @param {CategoryView} c
 * @returns {Partial<CategoryInput>}
 */
export function buildCategoryPatch(d, c) {
  const next = buildCategoryInput(d);
  /** @type {Partial<CategoryInput>} */
  const out = {};
  if (next.name !== c.name) {
    out.name = next.name;
    if (next.slug !== c.slug) out.slug = next.slug;
  }
  if (next.line !== c.line) out.line = next.line;
  if (next.sort !== c.sort) out.sort = next.sort;
  if (next.isActive !== c.isActive) out.isActive = next.isActive;
  return out;
}

/**
 * Delete is only allowed while no product uses the category.
 * @param {Pick<CategoryView, 'name' | 'productCount'>} c
 * @returns {{ ok: true } | { ok: false; message: string }}
 */
export function canDeleteCategory(c) {
  const n = c.productCount ?? 0;
  if (n <= 0) return { ok: true };
  return {
    ok: false,
    message: `${n} product${n === 1 ? ' uses' : 's use'} ${c.name}, so it can’t be deleted. Deactivate it instead: it leaves pickers and customer filters, and those products keep it.`,
  };
}

const LINE_RANK = { fresh: 0, grill: 1, mart: 2 };

/** By line, then sort, then name. @param {CategoryView[]} xs */
export function sortCategories(xs) {
  return xs.slice().sort((a, b) => {
    const la = a.line ? LINE_RANK[a.line] : 3;
    const lb = b.line ? LINE_RANK[b.line] : 3;
    if (la !== lb) return la - lb;
    if (a.sort !== b.sort) return a.sort - b.sort;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Categories a product editor may pick: active, same line, sorted. Keeps the product's current
 * category in the list (marked) even when it was deactivated, so an edit doesn't silently drop it.
 * @param {CategoryView[]} xs @param {ProductLine} line @param {{ id: string; name: string } | null} [current]
 * @returns {{ id: string; name: string; inactive: boolean }[]}
 */
export function pickerOptions(xs, line, current) {
  const list = sortCategories(xs.filter((c) => c.isActive && c.line === line)).map((c) => ({ id: c.id, name: c.name, inactive: false }));
  if (current && !list.some((c) => c.id === current.id)) list.push({ id: current.id, name: current.name, inactive: true });
  return list;
}
